/**
 * Wählbare Varianten eines Produkts für Auswahl-Oberflächen
 * (Bestellung/Angebot, Agenten-Katalog).
 *
 * Quelle der Wahrheit ist die Tabelle `product_variants` (Shopify-Sync +
 * Produkt-Editor). Die alte JSON-Spalte `products.variants` ist nur noch
 * Fallback für Produkte ohne Tabellenzeilen.
 *
 * `stockValue`: Die Lager-RPCs (reserve_stock/commit_stock) kennen nur die
 * alte JSON-Spalte. Eine Tabellen-Variante bekommt deshalb nur dann einen
 * stockValue, wenn es in der JSON-Spalte denselben `value` gibt. Sonst null,
 * damit der Bestand wie bisher auf Produktebene gebucht wird statt mit
 * `variant_not_found` abzubrechen.
 */

const DEFAULT_TITLES = new Set(['standard', 'default title', 'default']);

export function parseLegacyVariants(variants) {
  if (Array.isArray(variants)) return variants;
  if (typeof variants !== 'string' || !variants) return [];
  try {
    const parsed = JSON.parse(variants);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function variantLabel(v) {
  const title = (v.title || '').trim();
  if (title) return title;
  const opts = v.options && typeof v.options === 'object' ? Object.values(v.options).filter(Boolean) : [];
  return opts.length > 0 ? opts.join(' / ') : v.sku || 'Variante';
}

function isPlaceholderDefault(v) {
  return !v.sku && DEFAULT_TITLES.has((v.title || '').trim().toLowerCase());
}

function toNumberOrNull(n) {
  if (n === null || n === undefined || n === '') return null;
  const num = Number(n);
  return Number.isFinite(num) ? num : null;
}

function fromTable(product, rows) {
  const active = rows
    .filter((v) => v.aktiv !== false)
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const real = active.filter((v) => !isPlaceholderDefault(v));
  // Höchstens eine echte Variante = einfaches Produkt ohne Auswahl.
  if (real.length <= 1) return [];

  const legacyValues = new Set(parseLegacyVariants(product.variants).map((l) => l?.value).filter(Boolean));
  return real.map((v) => {
    const value = variantLabel(v);
    return {
      key: v.id,
      value,
      sku: v.sku || null,
      image: null,
      price: toNumberOrNull(v.b2b_preis),
      stockValue: legacyValues.has(value) ? value : null,
    };
  });
}

function fromLegacy(product) {
  return parseLegacyVariants(product.variants)
    .filter((l) => l && typeof l.value === 'string' && l.value.trim() !== '')
    .map((l) => ({
      key: `legacy:${l.value}`,
      value: l.value,
      sku: l.sku || null,
      image: l.image || null,
      price: toNumberOrNull(l.price),
      stockValue: l.value,
    }));
}

export function buildVariantOptions(product, variantRows) {
  const rows = Array.isArray(variantRows) ? variantRows : [];
  if (rows.length > 0) return fromTable(product, rows);
  return fromLegacy(product);
}

export function groupVariantsByProduct(rows) {
  const map = new Map();
  (rows || []).forEach((v) => {
    if (!v?.product_id) return;
    map.set(v.product_id, [...(map.get(v.product_id) || []), v]);
  });
  return map;
}
